export interface CallPreset {
  label: string;
  recipientName: string;
  recipientType: string;
  purpose: string;
  scriptGuidance: string;
  endConditions: string;
  /** One per line — specific outcomes to achieve */
  expectedOutcomes?: string;
  /** JSON-ish: one "if X → then Y" per line */
  contingencies?: string;
  /** Language for the call agent to open with. Defaults to English. */
  language?: string;
}

const PRESETS: CallPreset[] = [
  // ── Restaurants ──────────────────────────────────────────────────────
  {
    label: 'Restaurant — book table for 2',
    recipientName: 'The Ivy',
    recipientType: 'restaurant',
    purpose:
      'Book a table for 2 on Saturday evening around 7:30pm under the name Petrenz.',
    scriptGuidance:
      'Prefer outdoor terrace if available. One guest has a shellfish allergy (severe — carries EpiPen).',
    endConditions:
      'reservation confirmed\ntold they are fully booked\ntold to book online only',
    expectedOutcomes:
      'Table booked for 2 at 7:30pm Saturday under Petrenz\nAllergy noted on reservation\nConfirmation reference obtained',
    contingencies:
      'If 7:30 unavailable → accept any time between 7-9pm\nIf outdoor terrace full → accept indoor but mention preference for next time\nIf fully booked Saturday → ask about Friday or Sunday instead',
  },
  {
    label: 'Restaurant — book table for 4 with highchair',
    recipientName: 'Padella',
    recipientType: 'restaurant',
    purpose:
      'Book a table for 4 on Friday 17th in the evening. One child (age 2) needs a highchair.',
    scriptGuidance:
      'Peanut allergy in the group. Window table preferred if possible.',
    endConditions:
      'reservation confirmed\ntold they are fully booked\ntold to book online only',
    expectedOutcomes:
      'Table booked for 4 on Friday 17th evening\nHighchair confirmed\nPeanut allergy noted',
    contingencies:
      'If no highchairs available → ask if we can bring our own portable one\nIf evening is fully booked → ask about late lunch (3-4pm) as alternative',
  },
  {
    label: 'Restaurant — cancel reservation',
    recipientName: 'Dishoom',
    recipientType: 'restaurant',
    purpose:
      'Cancel our reservation for tomorrow evening. It was booked under Petrenz, party of 3.',
    scriptGuidance: '',
    endConditions:
      'cancellation confirmed\ntold there is no reservation on file',
  },
  {
    label: 'Restaurant — modify reservation time',
    recipientName: 'Barrafina',
    recipientType: 'restaurant',
    purpose:
      'Change our reservation from 7pm to 8:30pm. Same date (this Saturday), same party size (2), under Petrenz.',
    scriptGuidance: 'If 8:30 is not available, 8pm or 9pm also work.',
    endConditions:
      'modification confirmed\ntold the new time is not available and no alternative offered',
  },
  {
    label: 'Restaurant — ask about private dining',
    recipientName: 'Sketch',
    recipientType: 'restaurant',
    purpose:
      'Enquire about private dining options for a birthday dinner. 12 guests, looking at a Saturday in June.',
    scriptGuidance:
      'Budget is flexible. Interested in set menu options. The birthday person is turning 40.',
    endConditions:
      'received pricing and availability info\ntold to email for private events\ntold no private dining available',
  },
  {
    label: 'Restaurant — takeaway order',
    recipientName: 'Pizza Pilgrims',
    recipientType: 'restaurant',
    purpose: 'Place a takeaway order for collection in about 45 minutes.',
    scriptGuidance:
      '2x Margherita, 1x Nduja, 1x side of garlic bread, 1x tiramisu. If Nduja unavailable, swap for Diavola.',
    endConditions:
      'order confirmed with collection time\ntold they do not do takeaway\ntold to order via app only',
  },
  {
    label: 'Restaurant — check opening hours',
    recipientName: 'Hawksmoor',
    recipientType: 'restaurant',
    purpose:
      "Check if they're open on Bank Holiday Monday and what their hours are.",
    scriptGuidance:
      'Also ask if they accept walk-ins or if booking is required.',
    endConditions: 'received opening hours\nconfirmed they are closed',
  },
  {
    label: 'Restaurant — dietary requirements',
    recipientName: 'Ottolenghi',
    recipientType: 'restaurant',
    purpose:
      'We have an existing reservation for 6 on Thursday. Need to inform them about dietary requirements.',
    scriptGuidance:
      '1 vegan, 1 gluten-free, 1 nut allergy. Reservation under Petrenz.',
    endConditions:
      'dietary requirements noted\ntold there is no reservation on file',
  },

  // ── Hotels & Travel ──────────────────────────────────────────────────
  {
    label: 'Hotel — book room for weekend',
    recipientName: 'The Hoxton',
    recipientType: 'hotel',
    purpose:
      'Book a double room for 2 nights: Friday and Saturday this week. Checking in Friday afternoon, out Sunday morning.',
    scriptGuidance:
      'Quiet room preferred, away from the lift. Late checkout Sunday if possible.',
    endConditions:
      'booking confirmed with rate\ntold they are fully booked\ntold to book online',
    expectedOutcomes:
      'Double room booked for Fri-Sun\nRoom rate confirmed\nLate checkout confirmed or denied',
    contingencies:
      'If no double rooms → accept a superior/suite if under £250/night\nIf only one night available → book it and ask about waitlist for the other night\nIf late checkout not available → ask what the latest possible time is',
  },
  {
    label: 'Hotel — early check-in request',
    recipientName: 'Claridges',
    recipientType: 'hotel',
    purpose:
      'We have a reservation arriving next Tuesday. Ask if early check-in is possible — ideally by 11am.',
    scriptGuidance:
      'Reservation is under Petrenz. Happy to pay a supplement for early check-in if needed.',
    endConditions:
      'early check-in confirmed\ntold it cannot be guaranteed\noffered a time they can do',
  },
  {
    label: 'Hotel — cancel reservation',
    recipientName: 'Premier Inn Shoreditch',
    recipientType: 'hotel',
    purpose:
      'Cancel reservation for next weekend. Booking reference: PI-4829173.',
    scriptGuidance: 'Ask about cancellation fees if any.',
    endConditions:
      'cancellation confirmed\ntold about cancellation fee\ntold booking not found',
  },
  {
    label: 'Airline — change flight',
    recipientName: 'British Airways',
    recipientType: 'airline',
    purpose:
      'Change outbound flight BA283 on 22nd June from Heathrow to NYC. Want to move to the 23rd if possible, same route.',
    scriptGuidance:
      'Booking ref: ABC123. Flexible on time of day. Ask about fare difference.',
    endConditions:
      'flight changed and confirmed\ntold no availability on new date\ntold change fee and asked to call back',
  },
  {
    label: 'Airline — request wheelchair assistance',
    recipientName: 'EasyJet',
    recipientType: 'airline',
    purpose:
      'Add wheelchair assistance for our flight on the 15th. Booking ref EJX-99201. Passenger: Margaret Petrenz.',
    scriptGuidance:
      'She can walk short distances but needs assistance through the airport and boarding.',
    endConditions:
      'assistance confirmed\ntold to arrange at airport\ntold to use online form',
  },
  {
    label: 'Car rental — extend rental',
    recipientName: 'Enterprise',
    recipientType: 'car rental',
    purpose:
      'Extend current car rental by 3 days. Agreement number: 4A-291837.',
    scriptGuidance:
      'Currently due back Thursday, want to keep until Sunday. Ask about extra cost.',
    endConditions:
      'extension confirmed with new cost\ntold car is needed back for another booking\ngiven new return date',
  },
  {
    label: 'Train — refund for cancelled train',
    recipientName: 'Avanti West Coast',
    recipientType: 'train company',
    purpose:
      'Request a refund for a cancelled train on 3rd May. Booking ref: AWC-8827162.',
    scriptGuidance:
      'Train was the 14:30 from Euston to Manchester. Was cancelled with no replacement offered.',
    endConditions:
      'refund confirmed\ntold how to claim online\ntold refund not eligible',
  },
  {
    label: 'Travel agent — quote for holiday',
    recipientName: 'Trailfinders',
    recipientType: 'travel agent',
    purpose:
      'Get a quote for 10 days in Japan in October for 2 adults. Interested in Tokyo, Kyoto, and Osaka.',
    scriptGuidance:
      'Budget around £3-4k per person including flights and hotels. Prefer 4-star hotels. Interested in a rail pass.',
    endConditions:
      'received rough quote or range\ntold to visit branch in person\nscheduled callback with specialist',
  },
  {
    label: 'Airport transfer — book pickup',
    recipientName: 'Blacklane',
    recipientType: 'car service',
    purpose:
      'Book an airport transfer from Heathrow T5 to EC2A 4NE on Monday. Flight lands at 09:15.',
    scriptGuidance: '2 passengers, 2 large suitcases. Name on board: Petrenz.',
    endConditions:
      'transfer booked and confirmed\nreceived price quote\ntold to book online',
  },

  // ── Medical & Health ─────────────────────────────────────────────────
  {
    label: 'GP — book appointment',
    recipientName: 'Dr. Williams Surgery',
    recipientType: 'GP surgery',
    purpose:
      'Book a routine GP appointment for a medication review. Any day this week or next.',
    scriptGuidance:
      'Patient: Philipp Petrenz, DOB 15/03/1990. Prefer morning appointments if available.',
    endConditions:
      'appointment booked\ntold no availability and offered waitlist\ntold to book online',
    expectedOutcomes:
      'Appointment booked with date and time\nDoctor name confirmed',
    contingencies:
      'If no morning slots → accept afternoon but not after 4pm\nIf no appointments this week or next → ask to be put on cancellation list\nIf asked for reason → say routine medication review, not urgent',
  },
  {
    label: 'Dentist — book checkup',
    recipientName: 'Bupa Dental Care',
    recipientType: 'dentist',
    purpose:
      'Book a routine dental checkup and hygienist appointment. Ideally both on the same day.',
    scriptGuidance:
      'Patient: Philipp Petrenz. Last visit was about 8 months ago. Prefer afternoon slots.',
    endConditions:
      'appointment(s) booked\ntold next available is more than 4 weeks away\ntold to register first',
  },
  {
    label: 'Dentist — emergency toothache',
    recipientName: 'Emergency Dental Clinic',
    recipientType: 'dentist',
    purpose:
      'Need an emergency appointment today if possible. Sharp pain in lower right molar since last night.',
    scriptGuidance:
      'Patient: Philipp Petrenz. Pain is 7/10, worse with cold food. Taking ibuprofen.',
    endConditions:
      'appointment booked for today\noffered appointment within 48 hours\ntold to go to A&E',
  },
  {
    label: 'Optician — book eye test',
    recipientName: 'Specsavers',
    recipientType: 'optician',
    purpose: 'Book an eye test. Last one was over 2 years ago.',
    scriptGuidance:
      'Patient: Philipp Petrenz. Currently wears glasses for distance. Available any weekday.',
    endConditions:
      'appointment booked\ntold next available slot\ntold to book online',
  },
  {
    label: 'Physio — book initial assessment',
    recipientName: 'Pure Sports Medicine',
    recipientType: 'physiotherapist',
    purpose:
      'Book an initial physiotherapy assessment for a recurring lower back issue.',
    scriptGuidance:
      'Pain started 3 weeks ago after lifting. Available Tuesday or Thursday evenings.',
    endConditions:
      'appointment booked\ntold earliest availability\nasked to fill out online form first',
  },
  {
    label: 'Vet — book checkup for dog',
    recipientName: 'Medivet',
    recipientType: 'veterinary clinic',
    purpose: 'Book an annual checkup and vaccination booster for our dog.',
    scriptGuidance:
      "Dog's name: Biscuit, 4-year-old Labrador. Last vaccinations were 11 months ago. Owner: Petrenz.",
    endConditions:
      'appointment booked\ntold what to bring\ntold next availability',
  },

  // ── Home Services ────────────────────────────────────────────────────
  {
    label: 'Plumber — get quote for boiler service',
    recipientName: 'British Gas',
    recipientType: 'plumber',
    purpose:
      'Get a quote for annual boiler service. Vaillant ecoTEC Plus, about 5 years old.',
    scriptGuidance:
      'Property is a 2-bed flat in E2. Available weekday mornings. Ask about their HomeCare plans.',
    endConditions: 'received quote\nbooked service date\ntold to book online',
    expectedOutcomes:
      'Quote received for one-off service\nHomeCare plan pricing obtained\nEarliest available date confirmed',
    contingencies:
      'If they need the boiler model/serial number → say Vaillant ecoTEC Plus, approx 5 years old, do not have serial to hand\nIf they only do HomeCare (no one-off) → get the plan pricing and terms',
  },
  {
    label: 'Electrician — quote for socket installation',
    recipientName: 'Aspect Maintenance',
    recipientType: 'electrician',
    purpose:
      'Get a quote for installing 3 additional double sockets in a home office and 1 outdoor weatherproof socket.',
    scriptGuidance:
      'Victorian terrace, so walls may be lath and plaster. Available for site visit any morning.',
    endConditions:
      'received rough quote\nsite visit booked\ntold to send photos first',
  },
  {
    label: 'Locksmith — change locks',
    recipientName: 'KeyMaster London',
    recipientType: 'locksmith',
    purpose:
      'Get a quote for changing the front door lock. Just moved in, want fresh locks.',
    scriptGuidance:
      'Standard Yale + mortice deadlock. Available this week. Not urgent — can schedule ahead.',
    endConditions:
      'received quote\nappointment booked\ntold to send photo of current lock',
  },
  {
    label: 'Cleaner — book deep clean',
    recipientName: 'Hassle.com',
    recipientType: 'cleaning service',
    purpose:
      'Book a one-off deep clean for a 2-bedroom flat. End of tenancy situation.',
    scriptGuidance:
      'Property is in E8, about 750 sqft. Needs oven and carpet cleaning included. Available Thursday or Friday.',
    endConditions:
      'booking confirmed with price\nreceived quote\ntold to book online',
  },
  {
    label: 'Gardener — hedge trimming quote',
    recipientName: 'Fantastic Gardeners',
    recipientType: 'gardener',
    purpose:
      'Get a quote for hedge trimming and general garden tidy-up. About 20m of privet hedge.',
    scriptGuidance:
      'Small rear garden, maybe 8m x 5m. Some overgrown shrubs too. In N1 area.',
    endConditions:
      'received quote\nvisit scheduled for estimate\ntold minimum job size',
  },
  {
    label: 'Removals — quote for house move',
    recipientName: 'AnyVan',
    recipientType: 'removals company',
    purpose:
      'Get a quote for moving from a 2-bed flat in Hackney to a 3-bed house in Dulwich. Moving date: first weekend of July.',
    scriptGuidance:
      'About 40 boxes, sofa, dining table, bed, wardrobe. No piano. First floor flat (no lift) to ground floor house. Need packing service quote too.',
    endConditions:
      'received quote\nsite survey booked\ntold to fill out online inventory',
  },
  {
    label: 'Handyman — shelves and TV mount',
    recipientName: 'Handy',
    recipientType: 'handyman service',
    purpose:
      'Get a quote for putting up 4 floating shelves and wall-mounting a 55" TV.',
    scriptGuidance:
      'Brick walls. I have the shelves and TV bracket already. In SE1 area.',
    endConditions:
      'received quote with availability\nbooked appointment\ntold minimum booking time',
  },

  // ── Professional Services ────────────────────────────────────────────
  {
    label: 'Accountant — book tax consultation',
    recipientName: 'Smith & Partners',
    recipientType: 'accountant',
    purpose:
      'Book a consultation to discuss self-assessment tax return for 2024/25. First time using them.',
    scriptGuidance:
      'Have both employment income and freelance income. Need to understand what records to prepare.',
    endConditions:
      'consultation booked\nreceived fee information\ntold to send documents first',
  },
  {
    label: 'Solicitor — enquire about will writing',
    recipientName: 'Slater & Gordon',
    recipientType: 'solicitor',
    purpose:
      'Enquire about will writing services. Need a simple will — married couple, one child, straightforward estate.',
    scriptGuidance:
      'Want mirror wills for both partners. Ask about power of attorney at the same time.',
    endConditions:
      'received quote\nbooked initial appointment\ntold to fill out online form',
  },
  {
    label: 'Insurance — renew home insurance',
    recipientName: 'Admiral',
    recipientType: 'insurance company',
    purpose:
      'Home insurance renewal is due next week. Call to discuss quote and see if they can match a competitor price.',
    scriptGuidance:
      'Policy number: HM-9928371. Competitor quote is £180/year for same cover. Our renewal is £240.',
    endConditions:
      'received better price\ntold they cannot reduce\nagreed on new premium',
  },
  {
    label: 'Bank — dispute a transaction',
    recipientName: 'Monzo',
    recipientType: 'bank',
    purpose:
      'Dispute a transaction of £89.99 from 2 days ago. Merchant "XYZSHOP.COM" — did not make this purchase.',
    scriptGuidance:
      'Card was in my possession the whole time. Have not shared card details. No other suspicious activity.',
    endConditions:
      'dispute raised and reference provided\ntold to do it in-app\ntold process and timeline',
  },
  {
    label: 'Tailor — suit alteration',
    recipientName: 'Savile Row Tailors',
    recipientType: 'tailor',
    purpose:
      'Enquire about suit alteration — need trousers taken up and jacket sleeves shortened on a recent purchase.',
    scriptGuidance:
      'Suit is a Hugo Boss, bought off the rack. Need it for a wedding in 3 weeks.',
    endConditions:
      'received quote and turnaround time\nbooked fitting appointment\ntold to bring the suit in',
  },

  // ── Retail & Shopping ────────────────────────────────────────────────
  {
    label: 'Store — check stock availability',
    recipientName: 'John Lewis Oxford Street',
    recipientType: 'department store',
    purpose:
      'Check if they have the Dyson V15 Detect in stock for collection today.',
    scriptGuidance:
      'If not in stock, ask when next delivery expected. Also interested in the V12 as alternative.',
    endConditions:
      'confirmed in stock\ntold out of stock with restock date\ntold to check online',
  },
  {
    label: 'Furniture — delivery enquiry',
    recipientName: 'MADE.com',
    recipientType: 'furniture store',
    purpose:
      'Check delivery status for order #MD-773291. Was supposed to arrive last week.',
    scriptGuidance:
      'Ordered a sofa 8 weeks ago. Delivery was scheduled for last Thursday, nobody showed up.',
    endConditions:
      'given new delivery date\ntold to reschedule online\nescalated to complaints',
  },
  {
    label: 'Florist — order arrangement',
    recipientName: 'Bloom & Wild',
    recipientType: 'florist',
    purpose:
      "Order a flower arrangement for delivery on Thursday. It's for a colleague's birthday.",
    scriptGuidance:
      'Budget around £50-60. Bright and cheerful, not too formal. Card message: "Happy birthday Sarah! From the team"',
    endConditions:
      'order placed\ntold to order online\nreceived available options',
  },
  {
    label: 'Butcher — place weekend order',
    recipientName: 'The Ginger Pig',
    recipientType: 'butcher',
    purpose:
      'Place an order for weekend collection. Need a 2kg rib of beef and 8 lamb cutlets.',
    scriptGuidance:
      'Collection Saturday morning. Ask if they can French-trim the lamb cutlets. Under name Petrenz.',
    endConditions:
      'order confirmed\ntold to call back closer to weekend\ntold items unavailable',
  },
  {
    label: 'Bakery — order birthday cake',
    recipientName: 'Konditor',
    recipientType: 'bakery',
    purpose:
      'Order a birthday cake for collection next Saturday. Chocolate cake, serves 12.',
    scriptGuidance:
      'Need "Happy 40th Tom" written on it. Check if they can do a nut-free option.',
    endConditions:
      'order placed with collection time\ntold minimum notice required\ntold to order online',
  },

  // ── Utilities & Telecoms ─────────────────────────────────────────────
  {
    label: 'Internet — report outage',
    recipientName: 'BT',
    recipientType: 'ISP',
    purpose:
      'Report a broadband outage. Internet has been down since this morning.',
    scriptGuidance:
      'Account number: BT-992817. Have already restarted the router twice. Hub lights showing orange.',
    endConditions:
      'fault logged with reference number\ntold estimated fix time\ntold to try more troubleshooting',
  },
  {
    label: 'Internet — upgrade package',
    recipientName: 'Virgin Media',
    recipientType: 'ISP',
    purpose:
      'Enquire about upgrading broadband speed. Currently on M100, want to know about M500 or M1000 pricing.',
    scriptGuidance:
      'Account: VM-1827364. Contract ends next month. Interested in the best deal for existing customers.',
    endConditions:
      'received upgrade pricing\nagreed on new package\ntold current offers',
  },
  {
    label: 'Mobile — unlock phone',
    recipientName: 'Vodafone',
    recipientType: 'mobile network',
    purpose:
      'Request phone unlock. Contract ended 2 months ago, want to switch networks.',
    scriptGuidance:
      'Account: 07700900123. Device is iPhone 14 Pro. Been a customer for 4 years.',
    endConditions:
      'unlock request submitted\ntold it will be done in X days\ntold requirements not met',
  },
  {
    label: 'Energy — submit meter reading',
    recipientName: 'Octopus Energy',
    recipientType: 'energy provider',
    purpose: 'Submit a meter reading. Electricity: 45821. Gas: 1293.',
    scriptGuidance:
      'Account number: A-5E9B2C91. Also ask about switching to a fixed rate tariff.',
    endConditions:
      'reading accepted\ntariff options explained\ntold to submit online',
  },
  {
    label: 'Water — report leak',
    recipientName: 'Thames Water',
    recipientType: 'water company',
    purpose:
      'Report a water leak on the pavement outside our property. Been running for 2 days.',
    scriptGuidance:
      'Address: 42 Elm Road, E8 2NP. The leak is by the water meter cover near the front gate.',
    endConditions:
      'report logged with reference\ntold timeline for inspection\ntold it is an internal issue',
  },

  // ── Events & Entertainment ───────────────────────────────────────────
  {
    label: 'Theatre — book tickets',
    recipientName: 'Old Vic Box Office',
    recipientType: 'theatre',
    purpose:
      'Book 2 tickets for next Saturday evening performance. Best available seats.',
    scriptGuidance:
      'Prefer stalls or dress circle. Budget up to £100 per ticket. Ask about any discounts for booking by phone.',
    endConditions:
      'tickets booked\ntold performance is sold out\ntold to book online for best prices',
  },
  {
    label: 'Venue — enquire about event space',
    recipientName: 'The Shard',
    recipientType: 'event venue',
    purpose:
      'Enquire about hiring a private space for a corporate event. 50 people, evening drinks reception.',
    scriptGuidance:
      "Looking at a Friday evening in September. Need catering options too. It's a product launch.",
    endConditions:
      'received availability and pricing\nsite visit scheduled\ntold to email events team',
  },
  {
    label: 'Cinema — check screening times',
    recipientName: 'Curzon Soho',
    recipientType: 'cinema',
    purpose:
      'Check what times they are showing the new Tarantino film this weekend.',
    scriptGuidance:
      'Prefer evening showings. Ask if they have any premium/Dolby screenings.',
    endConditions:
      'received showing times\ntold film not showing there\ntold to check website',
  },
  {
    label: 'Museum — book group visit',
    recipientName: 'Natural History Museum',
    recipientType: 'museum',
    purpose:
      "Book a group visit for 15 children (ages 7-8) and 4 adults. It's a school trip.",
    scriptGuidance:
      'Looking at a Wednesday in the next month. Interested in dinosaur gallery guided tour if available.',
    endConditions:
      'group booking confirmed\ntold guided tour availability\ntold to apply online for groups',
  },
  {
    label: 'Spa — book treatment',
    recipientName: 'ESPA Life at Corinthia',
    recipientType: 'spa',
    purpose: "Book a couples' massage for next Friday afternoon.",
    scriptGuidance:
      '60 or 90 minutes preferred. Ask about any spa day packages that include pool access.',
    endConditions:
      'treatment booked\ntold to book online\nreceived available times and prices',
  },
  {
    label: 'Golf course — book tee time',
    recipientName: 'Sunningdale Golf Club',
    recipientType: 'golf club',
    purpose:
      'Book a tee time for 4 players on Saturday morning. Prefer before 9am.',
    scriptGuidance:
      'Guest booking (not a member). Ask about green fees and whether buggies are available.',
    endConditions:
      'tee time booked\ntold members only at weekends\nreceived available times and fees',
  },

  // ── Education & Childcare ────────────────────────────────────────────
  {
    label: 'Nursery — enquire about places',
    recipientName: 'Bright Horizons',
    recipientType: 'nursery',
    purpose:
      'Enquire about nursery places for a 2-year-old starting in September. 3 days per week.',
    scriptGuidance:
      'Preferred days: Monday, Wednesday, Friday. Ask about fees, settling-in process, and waiting list.',
    endConditions:
      'received fee info and availability\nput on waiting list\nscheduled visit',
  },
  {
    label: 'Tutor — book trial lesson',
    recipientName: 'MyTutor',
    recipientType: 'tutoring service',
    purpose:
      'Book a trial maths lesson for a Year 10 student. Preparing for GCSEs.',
    scriptGuidance:
      'Student struggles with algebra and geometry. Available Tuesday or Thursday after 4pm.',
    endConditions:
      'trial lesson booked\nmatched with tutor\ntold to sign up online first',
  },
  {
    label: 'Driving school — book lessons',
    recipientName: 'RED Driving School',
    recipientType: 'driving school',
    purpose:
      'Book a block of 10 driving lessons. Complete beginner, no previous experience.',
    scriptGuidance:
      'Available weekday evenings and Saturday mornings. Pickup from E8 postcode.',
    endConditions:
      'lessons booked\nreceived pricing for block\ntold instructor availability',
  },

  // ── Fitness & Wellness ───────────────────────────────────────────────
  {
    label: 'Gym — cancel membership',
    recipientName: 'PureGym',
    recipientType: 'gym',
    purpose: 'Cancel gym membership. Member number: PG-229183.',
    scriptGuidance:
      'Moving out of the area. Ask about notice period and final payment date.',
    endConditions:
      'cancellation confirmed\ntold about notice period\ntold to cancel online',
  },
  {
    label: 'Yoga studio — book class pass',
    recipientName: 'Triyoga',
    recipientType: 'yoga studio',
    purpose:
      'Enquire about class pass options. Want to try a few different classes before committing to membership.',
    scriptGuidance:
      'Intermediate level. Interested in Vinyasa and Yin yoga. Prefer evening classes in Camden studio.',
    endConditions:
      'received pass options and pricing\npurchased class pass\ntold to book online',
  },
  {
    label: 'Swimming pool — book lane swim',
    recipientName: 'London Fields Lido',
    recipientType: 'swimming pool',
    purpose:
      'Ask about lane swimming times and whether I need to pre-book or can turn up.',
    scriptGuidance:
      'Interested in early morning sessions before work (6-8am). Ask about season tickets.',
    endConditions:
      'received swim times\ntold booking process\ntold about memberships',
  },
  {
    label: 'Tennis court — book court time',
    recipientName: 'Islington Tennis Centre',
    recipientType: 'tennis centre',
    purpose:
      'Book a tennis court for this Sunday morning. 1 hour, outdoor if weather is fine.',
    scriptGuidance:
      'Prefer 9am or 10am slot. Ask if they have rackets for hire (partner forgot theirs).',
    endConditions:
      'court booked\ntold availability\ntold they are fully booked',
  },

  // ── Automotive ───────────────────────────────────────────────────────
  {
    label: 'Garage — book MOT and service',
    recipientName: 'Kwik Fit',
    recipientType: 'garage',
    purpose: 'Book an MOT and full service. Car: 2021 VW Golf, reg AB21 XYZ.',
    scriptGuidance:
      'MOT due end of this month. Available any weekday, can drop car off in the morning.',
    endConditions:
      'appointment booked\ntold earliest availability\nreceived pricing',
  },
  {
    label: 'Garage — collect repaired car',
    recipientName: 'Halfords Autocentre',
    recipientType: 'garage',
    purpose:
      'Check if the car is ready for collection. Dropped it off Monday for brake pad replacement.',
    scriptGuidance: 'Reg: CD19 ABC. Name: Petrenz. Ask about the total cost.',
    endConditions:
      'told car is ready with final cost\ntold it needs more time\ntold about additional work needed',
  },
  {
    label: 'Tyre shop — book tyre replacement',
    recipientName: 'National Tyres',
    recipientType: 'tyre shop',
    purpose: 'Book appointment to replace 2 front tyres. 2021 VW Golf.',
    scriptGuidance:
      'Tyre size: 225/45 R17. Want mid-range tyres (not budget). Available Thursday afternoon.',
    endConditions:
      'appointment booked with price\ntold tyre availability\ntold to check online for pricing',
  },
  {
    label: 'Car wash — enquire about valet',
    recipientName: 'Autoglym',
    recipientType: 'car valeting',
    purpose:
      'Enquire about a full interior and exterior valet. How long does it take and can I wait?',
    scriptGuidance:
      'Car is a large SUV. Particularly dirty interior (kids + dog). Want the full works.',
    endConditions:
      'received pricing and duration\nbooked appointment\ntold to bring car in anytime',
  },

  // ── Government & Admin ───────────────────────────────────────────────
  {
    label: 'Council — bulky waste collection',
    recipientName: 'Hackney Council',
    recipientType: 'local council',
    purpose:
      'Book a bulky waste collection. Need to dispose of a sofa and a broken washing machine.',
    scriptGuidance:
      'Address: 42 Elm Road, E8 2NP. Items are inside, can bring to pavement on collection day.',
    endConditions:
      'collection booked with date\ntold how to book online\ntold about fees',
  },
  {
    label: 'Council — parking permit enquiry',
    recipientName: 'Islington Council',
    recipientType: 'local council',
    purpose:
      'Enquire about resident parking permits. Just moved to the area and need to understand the process.',
    scriptGuidance:
      'New address is in Zone F. Have a V5 document and utility bill as proof.',
    endConditions:
      'received application process info\ntold to apply online\ntold about costs and zones',
  },
  {
    label: 'Passport office — check application status',
    recipientName: 'HM Passport Office',
    recipientType: 'government office',
    purpose:
      'Check status of passport renewal application submitted 5 weeks ago.',
    scriptGuidance:
      'Application reference: PEX-839271-PP. Applied online with photos.',
    endConditions:
      'received status update\ntold processing time\ntold to check online tracker',
  },

  // ── Food & Drink ─────────────────────────────────────────────────────
  {
    label: 'Catering — quote for party',
    recipientName: 'Detox Kitchen',
    recipientType: 'caterer',
    purpose:
      'Get a quote for catering a house party. 25 guests, mix of canapés and bowl food.',
    scriptGuidance:
      'Saturday evening in 3 weeks. Need vegetarian and vegan options. Budget ~£40-50pp. No shellfish.',
    endConditions:
      'received quote\nscheduled tasting or call\ntold to email for custom events',
  },
  {
    label: 'Wine merchant — order case',
    recipientName: 'Berry Bros & Rudd',
    recipientType: 'wine merchant',
    purpose:
      'Order a mixed case of 12 bottles. Half red, half white. Spending around £200 total.',
    scriptGuidance:
      'Prefer French wines. Reds: medium body, Burgundy style. Whites: dry, crisp. For a dinner party.',
    endConditions:
      'order placed\nreceived recommendations\ntold to browse online',
  },
  {
    label: 'Coffee roaster — set up subscription',
    recipientName: 'Square Mile Coffee',
    recipientType: 'coffee roaster',
    purpose:
      'Enquire about setting up a coffee subscription. 500g every 2 weeks.',
    scriptGuidance:
      'Drink filter coffee mostly. Prefer light-medium roast, fruity/floral notes. Have a V60 and Aeropress.',
    endConditions:
      'subscription set up\nreceived options and pricing\ntold to set up online',
  },

  // ── Personal Care ────────────────────────────────────────────────────
  {
    label: 'Barber — book haircut',
    recipientName: 'Ruffians',
    recipientType: 'barber',
    purpose: 'Book a haircut for Saturday. Any time between 10am and 2pm.',
    scriptGuidance:
      "Prefer the same barber as last time (Jake) if he's available.",
    endConditions:
      'appointment booked\ntold to book online\ngiven next availability',
  },
  {
    label: 'Dry cleaner — check order status',
    recipientName: 'Timpson',
    recipientType: 'dry cleaner',
    purpose:
      'Check if the suit and 3 shirts dropped off last Monday are ready. Ticket number: 4829.',
    scriptGuidance: 'Name: Petrenz. Was told 5 working days.',
    endConditions:
      'told items are ready\ntold when they will be ready\ntold ticket not found',
  },
  {
    label: 'Tailor — enquire about bespoke shirt',
    recipientName: 'Emma Willis',
    recipientType: 'shirt maker',
    purpose:
      'Enquire about bespoke shirts. Want to order 3 white and 2 blue business shirts.',
    scriptGuidance:
      'Want to understand the process — measurement appointment, fabric choices, timeline, and pricing.',
    endConditions:
      'received pricing info\nbooked measurement appointment\ntold to visit the shop',
  },

  // ── Pets ─────────────────────────────────────────────────────────────
  {
    label: 'Dog groomer — book appointment',
    recipientName: 'Doggy Style',
    recipientType: 'dog groomer',
    purpose:
      'Book a grooming appointment for a Labrador. Full wash, dry, and nail trim.',
    scriptGuidance:
      "Dog's name is Biscuit. He's good with groomers. Available any weekday.",
    endConditions:
      'appointment booked\ntold next availability\ntold to book online',
  },
  {
    label: 'Kennel — book boarding',
    recipientName: 'Barking Mad',
    recipientType: 'dog boarding',
    purpose: 'Book dog boarding for 5 nights: 14th-19th August. One Labrador.',
    scriptGuidance:
      'Biscuit, 4 years old, neutered male. Up to date on vaccinations. Ask what docs they need.',
    endConditions:
      'boarding confirmed\ntold they are full\ntold what to bring for the dog',
  },
  {
    label: 'Pet shop — order food',
    recipientName: 'Pets at Home',
    recipientType: 'pet shop',
    purpose:
      'Check if they have Royal Canin Labrador Adult in 12kg bags in stock.',
    scriptGuidance:
      'If not in stock, ask when next delivery expected. Also need 2 bags of dental chews.',
    endConditions:
      'confirmed in stock\ntold when restock expected\ntold to check online',
  },

  // ── Technology & Repairs ─────────────────────────────────────────────
  {
    label: 'Apple — book Genius Bar',
    recipientName: 'Apple Regent Street',
    recipientType: 'Apple Store',
    purpose:
      'Book a Genius Bar appointment. MacBook Pro screen has a vertical line appearing.',
    scriptGuidance:
      'MacBook Pro 14" M3, bought 10 months ago (should be under warranty). Available any afternoon this week.',
    endConditions:
      'appointment booked\ntold to book online\ntold to use Apple Support app',
  },
  {
    label: 'Phone repair — screen replacement quote',
    recipientName: 'iSmash',
    recipientType: 'phone repair',
    purpose:
      'Get a quote for iPhone 15 Pro screen replacement. Cracked in bottom corner but still works.',
    scriptGuidance:
      'Ask about warranty on the repair and how long it takes. Can I wait while they do it?',
    endConditions:
      'received quote and timeline\ntold to come in for assessment\ntold to book online',
  },
  {
    label: 'IT support — printer not connecting',
    recipientName: 'HP Support',
    recipientType: 'tech support',
    purpose:
      'HP LaserJet Pro is not connecting to WiFi after router change. Need help reconfiguring.',
    scriptGuidance:
      'Model: HP LaserJet Pro M404dn. New router is a BT Smart Hub 2. Printer worked fine before.',
    endConditions:
      'issue resolved\nscheduled callback\ntold to try specific troubleshooting steps',
  },

  // ── Financial ────────────────────────────────────────────────────────
  {
    label: 'Mortgage broker — rate enquiry',
    recipientName: 'London & Country',
    recipientType: 'mortgage broker',
    purpose:
      'Enquire about remortgage options. Current fixed rate ends in 4 months.',
    scriptGuidance:
      'Property value ~£650k, outstanding mortgage ~£350k. Currently on 3.2% with Halifax. Income: £95k. Want to know best rates available.',
    endConditions:
      'received rate options\nscheduled full consultation\ntold to apply online',
  },
  {
    label: 'Financial advisor — pension review',
    recipientName: 'St. James Place',
    recipientType: 'financial advisor',
    purpose:
      'Book an appointment to review pension arrangements. Have 3 workplace pensions from different jobs, want to consolidate.',
    scriptGuidance:
      'Total value around £180k across all pots. Age 35. Want to understand consolidation options and fees.',
    endConditions:
      'appointment booked\ntold initial consultation is free\ntold to gather statements first',
  },
  {
    label: 'Currency exchange — large transfer',
    recipientName: 'Wise',
    recipientType: 'money transfer',
    purpose:
      'Enquire about making a large GBP to EUR transfer. £50,000 for a property purchase abroad.',
    scriptGuidance:
      'Need to understand their process for large transfers, any limits, and what ID is needed.',
    endConditions:
      'received rate and process info\ntold about limits and requirements\ntold to do it online',
  },

  // ── Subscriptions & Memberships ──────────────────────────────────────
  {
    label: 'Magazine — cancel subscription',
    recipientName: 'The Economist',
    recipientType: 'publication',
    purpose: 'Cancel digital subscription. Account email: philipp@example.com.',
    scriptGuidance:
      "Reason: not reading it enough. If they offer a discount to stay, I'm interested if it's 50% off or more.",
    endConditions:
      'subscription cancelled\naccepted retention offer\ntold notice period',
  },
  {
    label: 'Club — enquire about membership',
    recipientName: 'Soho House',
    recipientType: 'members club',
    purpose: 'Enquire about membership options and the application process.',
    scriptGuidance:
      'Interested in the Under 27 membership or standard. Work in tech. Live in East London.',
    endConditions:
      'received membership info\ntold about waiting list\ntold how to apply',
  },

  // ── Charity & Community ──────────────────────────────────────────────
  {
    label: 'Charity shop — arrange furniture donation',
    recipientName: 'British Heart Foundation',
    recipientType: 'charity',
    purpose:
      'Arrange collection of furniture donation. Sofa (3-seater, good condition) and bookcase.',
    scriptGuidance:
      'Ground floor flat, easy access. Available any day next week for collection.',
    endConditions:
      'collection booked\ntold they cannot collect from this area\ntold what they accept',
  },
  {
    label: 'Library — renew books',
    recipientName: 'Hackney Library',
    recipientType: 'library',
    purpose:
      'Renew 3 books that are due tomorrow. Library card number: HK-291837.',
    scriptGuidance:
      'Books are: "Klara and the Sun", "Project Hail Mary", "The Thursday Murder Club".',
    endConditions:
      'books renewed\ntold they have holds so cannot renew\ntold how to renew online',
  },

  // ── Trades & Construction ────────────────────────────────────────────
  {
    label: 'Builder — quote for loft conversion',
    recipientName: 'Simply Loft',
    recipientType: 'builder',
    purpose:
      'Get an initial quote for a dormer loft conversion. Victorian terraced house in E8.',
    scriptGuidance:
      'Want a bedroom with en-suite bathroom. House is mid-terrace, 5.5m wide. No party wall agreement yet.',
    endConditions:
      'received rough quote range\nsite visit booked\ntold to send plans/photos',
  },
  {
    label: 'Kitchen fitter — quote for new kitchen',
    recipientName: 'Wren Kitchens',
    recipientType: 'kitchen company',
    purpose:
      'Book a design appointment for a new kitchen. Renovating a 1930s semi.',
    scriptGuidance:
      'Kitchen is about 4m x 3m. Want handleless units, quartz worktop, integrated appliances. Budget: £15-20k installed.',
    endConditions:
      'design appointment booked\ntold to visit showroom\nreceived available dates',
  },
  {
    label: 'Window cleaner — quote for house',
    recipientName: 'Crystal Clear Windows',
    recipientType: 'window cleaner',
    purpose:
      'Get a quote for regular window cleaning. 3-bed terraced house, front and back.',
    scriptGuidance:
      'About 12 windows total, including 2 skylights. Looking for monthly service.',
    endConditions:
      'received quote\nagreed on schedule\ntold to contact someone else for skylights',
  },
  {
    label: 'Painter — quote for interior painting',
    recipientName: "Mike's Decorating",
    recipientType: 'painter/decorator',
    purpose:
      'Get a quote for painting the living room and hallway. About 40sqm total wall area.',
    scriptGuidance:
      'Walls currently dark blue, want to go to light grey. Ceilings and woodwork are fine. Have chosen the paint (Farrow & Ball).',
    endConditions:
      'received quote\nbooked start date\ntold to send photos for estimate',
  },

  // ── Miscellaneous Services ───────────────────────────────────────────
  {
    label: 'Photographer — book portrait session',
    recipientName: 'Venture Photography',
    recipientType: 'photographer',
    purpose: 'Book a family portrait session. 2 adults and a toddler.',
    scriptGuidance:
      'Prefer outdoor/park setting if weather allows. Available weekends. Ask about packages that include prints.',
    endConditions:
      'session booked\nreceived package options and pricing\ntold to book online',
  },
  {
    label: 'Courier — book same-day delivery',
    recipientName: 'CitySprint',
    recipientType: 'courier',
    purpose:
      'Book a same-day courier from E8 to WC2. Small parcel (A4 envelope with documents).',
    scriptGuidance:
      'Needs to arrive by 3pm. Collection from flat (buzzer 4B). Delivery to reception desk at the office.',
    endConditions:
      'booking confirmed with price\ntold too late for same-day\nreceived available time slots',
  },
  {
    label: 'Storage — enquire about unit',
    recipientName: 'Big Yellow Storage',
    recipientType: 'self storage',
    purpose:
      'Enquire about storage units. Need something for about 3-4 months during a house renovation.',
    scriptGuidance:
      'Probably need about 50-75 sqft. Mix of boxes and furniture. Ask about access hours and insurance.',
    endConditions:
      'received pricing and unit options\nbooked or reserved unit\ntold to view available units',
  },
  {
    label: 'Key cutting — car key replacement',
    recipientName: 'Timpson',
    recipientType: 'key cutting',
    purpose: 'Enquire about getting a spare car key made. 2021 VW Golf.',
    scriptGuidance:
      'Only have one key and want a spare. Ask about cost and how long it takes. Do I need to bring the car?',
    endConditions:
      'received price and process info\nbooked appointment\ntold they cannot do that car model',
  },
  {
    label: 'Cobbler — shoe repair quote',
    recipientName: 'The Shoe Healer',
    recipientType: 'cobbler',
    purpose:
      'Get a quote for resoling a pair of leather boots. Church Chetnole, Dainite sole replacement.',
    scriptGuidance:
      'Also want new heel tips and edge refinishing. Ask about turnaround time.',
    endConditions:
      'received quote and timeline\ntold to bring them in\ntold they do not do that type of sole',
  },
  {
    label: 'Frame shop — custom framing quote',
    recipientName: 'John Jones',
    recipientType: 'picture framer',
    purpose: 'Get a quote for framing 2 art prints. Both are A2 size.',
    scriptGuidance:
      'Want simple black wooden frames with white mounts. Museum glass if budget allows. Prints are valuable limited editions.',
    endConditions:
      'received quote\ntold to bring prints in for assessment\ngiven turnaround time',
  },
  {
    label: 'Piano tuner — book tuning',
    recipientName: "Bill's Piano Service",
    recipientType: 'piano tuner',
    purpose:
      'Book a piano tuning. Upright piano, last tuned about 18 months ago.',
    scriptGuidance:
      'Yamaha U1. Sounds slightly flat. Available weekday mornings. Piano is in ground floor living room.',
    endConditions:
      'appointment booked\ntold earliest availability\ntold to check online',
  },
  {
    label: 'Jeweller — ring resize enquiry',
    recipientName: 'Hatton Garden Jewellers',
    recipientType: 'jeweller',
    purpose:
      'Enquire about resizing an engagement ring. Gold band with diamond. Need to go up half a size.',
    scriptGuidance:
      'Currently size L, need L.5. Ring is about 5 years old. Ask about timeline and whether the stone needs removing.',
    endConditions:
      'received quote and timeline\ntold to bring ring in\ntold it may not be possible due to setting',
  },
  {
    label: 'Watch repair — battery replacement',
    recipientName: 'Watches of Switzerland',
    recipientType: 'watch shop',
    purpose:
      'Ask about replacing the battery in a Tag Heuer Aquaracer. Quartz model.',
    scriptGuidance:
      'Watch is about 8 years old. Ask if they also do a pressure test (it is a dive watch).',
    endConditions:
      'received price for battery + pressure test\ntold to bring it in\ntold to send to Tag Heuer directly',
  },
  {
    label: 'Cattery — book cat boarding',
    recipientName: 'The Cat Hotel',
    recipientType: 'cattery',
    purpose:
      'Book cat boarding for 1 week: 20th-27th December. One indoor cat.',
    scriptGuidance:
      "Cat's name: Miso, 6-year-old British Shorthair, neutered female. Fully vaccinated. Quite shy with new people.",
    endConditions:
      'boarding confirmed\ntold they are full for Christmas\ntold what to bring',
  },

  // ── Non-English Presets ─────────────────────────────────────────────────

  // German
  {
    label: '🇩🇪 Restaurant — Tisch reservieren (German)',
    recipientName: 'Zur Letzten Instanz',
    recipientType: 'restaurant',
    purpose:
      'Einen Tisch für 2 Personen am Samstagabend um 19:30 Uhr reservieren. Name: Petrenz.',
    scriptGuidance:
      'Draußen bevorzugt falls möglich. Ein Gast hat eine Nussallergie.',
    endConditions: 'Reservierung bestätigt\nausgebucht\nnur online buchbar',
    expectedOutcomes:
      'Tisch für 2 am Samstag 19:30 reserviert\nAllergie vermerkt',
    contingencies:
      'Falls 19:30 nicht verfügbar → jede Zeit zwischen 19-21 Uhr akzeptieren\nFalls ausgebucht → nach Freitag oder Sonntag fragen',
    language: 'German',
  },
  {
    label: '🇩🇪 Arzt — Termin vereinbaren (German)',
    recipientName: 'Praxis Dr. Müller',
    recipientType: 'Arztpraxis',
    purpose:
      'Einen Routinetermin für eine Vorsorgeuntersuchung vereinbaren. Diese oder nächste Woche.',
    scriptGuidance:
      'Patient: Philipp Petrenz, geb. 15.03.1990. Vormittags bevorzugt.',
    endConditions:
      'Termin vereinbart\nkeine Termine frei, Warteliste angeboten\nonline buchen',
    expectedOutcomes:
      'Termin mit Datum und Uhrzeit vereinbart\nName des Arztes bestätigt',
    contingencies:
      'Falls kein Vormittagstermin → Nachmittag akzeptieren, aber nicht nach 16 Uhr\nFalls keine Termine verfügbar → auf die Absageliste setzen lassen',
    language: 'German',
  },
  {
    label: '🇩🇪 Handwerker — Angebot für Malerarbeiten (German)',
    recipientName: 'Malermeister Schmidt',
    recipientType: 'Maler',
    purpose:
      'Ein Angebot für das Streichen von Wohnzimmer und Flur einholen. Ca. 40qm Wandfläche.',
    scriptGuidance:
      'Wände aktuell dunkelblau, sollen hellgrau werden. Decken und Holzwerk sind in Ordnung.',
    endConditions: 'Angebot erhalten\nTermin vereinbart\nFotos zuerst schicken',
    expectedOutcomes: 'Preisangebot erhalten\nFrühester Starttermin erfahren',
    contingencies:
      'Falls Besichtigung nötig → Termin vormittags unter der Woche vereinbaren\nFalls Farbe gestellt werden soll → wir haben die Farbe bereits (Farrow & Ball)',
    language: 'German',
  },

  // French
  {
    label: '🇫🇷 Restaurant — réserver une table (French)',
    recipientName: 'Le Comptoir',
    recipientType: 'restaurant',
    purpose:
      'Réserver une table pour 2 personnes samedi soir vers 20h. Au nom de Petrenz.',
    scriptGuidance:
      'Terrasse de préférence si possible. Un invité est intolérant au gluten.',
    endConditions:
      'réservation confirmée\ncomplet\nréservation en ligne uniquement',
    expectedOutcomes:
      'Table réservée pour 2 samedi 20h\nIntolérance au gluten notée',
    contingencies:
      'Si 20h pas disponible → accepter entre 19h30 et 21h\nSi complet samedi → demander vendredi ou dimanche',
    language: 'French',
  },
  {
    label: '🇫🇷 Médecin — prendre rendez-vous (French)',
    recipientName: 'Cabinet Dr. Dupont',
    recipientType: 'cabinet médical',
    purpose:
      "Prendre un rendez-vous pour un renouvellement d'ordonnance. Cette semaine ou la semaine prochaine.",
    scriptGuidance:
      'Patient: Philipp Petrenz, né le 15/03/1990. Préférence pour le matin.',
    endConditions:
      'rendez-vous pris\npas de disponibilité\nprendre rdv en ligne',
    expectedOutcomes:
      'Rendez-vous confirmé avec date et heure\nNom du médecin confirmé',
    contingencies:
      "Si pas de créneau le matin → accepter l'après-midi avant 16h\nSi aucune disponibilité → demander à être mis sur liste d'attente",
    language: 'French',
  },
  {
    label: '🇫🇷 Hôtel — réserver une chambre (French)',
    recipientName: 'Hôtel Le Marais',
    recipientType: 'hôtel',
    purpose:
      'Réserver une chambre double pour 2 nuits: vendredi et samedi. Arrivée vendredi après-midi.',
    scriptGuidance:
      'Chambre calme de préférence, pas côté rue. Demander si le petit-déjeuner est inclus.',
    endConditions:
      'réservation confirmée avec tarif\ncomplet\nréserver en ligne',
    expectedOutcomes:
      'Chambre réservée ven-dim\nTarif par nuit confirmé\nPetit-déjeuner clarifié',
    contingencies:
      "Si pas de chambre double → accepter une supérieure si moins de 200€/nuit\nSi une seule nuit disponible → la prendre et demander si l'autre se libère",
    language: 'French',
  },

  // Spanish
  {
    label: '🇪🇸 Restaurante — reservar mesa (Spanish)',
    recipientName: 'Casa Lucio',
    recipientType: 'restaurante',
    purpose:
      'Reservar una mesa para 4 personas el sábado a las 21h. A nombre de Petrenz.',
    scriptGuidance:
      'Uno de los comensales es celíaco. Preferencia por mesa interior, no cerca de la cocina.',
    endConditions: 'reserva confirmada\ncompleto\nsolo reservas online',
    expectedOutcomes:
      'Mesa reservada para 4 el sábado 21h\nAlergia al gluten anotada',
    contingencies:
      'Si las 21h no están disponibles → aceptar entre 20:30 y 22:00\nSi están completos el sábado → preguntar por el viernes o domingo',
    language: 'Spanish',
  },
  {
    label: '🇪🇸 Dentista — pedir cita (Spanish)',
    recipientName: 'Clínica Dental Sonríe',
    recipientType: 'clínica dental',
    purpose:
      'Pedir cita para una limpieza dental y revisión. Cualquier día de esta semana o la próxima.',
    scriptGuidance:
      'Paciente: Philipp Petrenz. Última visita hace unos 10 meses. Preferencia por las tardes.',
    endConditions: 'cita confirmada\nsin disponibilidad\npedir cita por la web',
    expectedOutcomes:
      'Cita confirmada con fecha y hora\nPrecio de la limpieza confirmado',
    contingencies:
      'Si no hay tardes disponibles → aceptar por la mañana pero no antes de las 10h\nSi no hay citas esta quincena → pedir que nos llamen si hay cancelación',
    language: 'Spanish',
  },

  // Italian
  {
    label: '🇮🇹 Ristorante — prenotare un tavolo (Italian)',
    recipientName: 'Trattoria Da Enzo',
    recipientType: 'ristorante',
    purpose:
      'Prenotare un tavolo per 2 persone sabato sera alle 20:30. Nome: Petrenz.',
    scriptGuidance:
      'Preferenza per tavolo in terrazza. Un ospite è vegetariano.',
    endConditions:
      'prenotazione confermata\ntutto esaurito\nsolo prenotazioni online',
    expectedOutcomes:
      'Tavolo prenotato per 2 sabato 20:30\nOpzione vegetariana confermata',
    contingencies:
      'Se 20:30 non disponibile → accettare tra le 20 e le 21:30\nSe tutto esaurito sabato → chiedere per venerdì o domenica',
    language: 'Italian',
  },
  {
    label: '🇮🇹 Meccanico — prenotare tagliando (Italian)',
    recipientName: 'Autofficina Rossi',
    recipientType: 'officina meccanica',
    purpose: 'Prenotare un tagliando completo e revisione. Auto: VW Golf 2021.',
    scriptGuidance:
      "Disponibile qualsiasi giorno feriale, posso lasciare l'auto la mattina. Targa: AB123CD.",
    endConditions:
      'appuntamento fissato\nricevuto preventivo\nprenotare online',
    expectedOutcomes:
      'Appuntamento fissato con data\nPreventivo ricevuto\nTempo di consegna stimato',
    contingencies:
      "Se serve più di un giorno → va bene, chiedere auto sostitutiva\nSe non fanno VW → chiedere se possono consigliare un'altra officina",
    language: 'Italian',
  },

  // Portuguese
  {
    label: '🇵🇹 Restaurante — reservar mesa (Portuguese)',
    recipientName: 'Cervejaria Ramiro',
    recipientType: 'restaurante',
    purpose:
      'Reservar uma mesa para 3 pessoas no sábado à noite, por volta das 20h. Nome: Petrenz.',
    scriptGuidance:
      'Um dos convidados tem alergia a marisco. Preferência por mesa junto à janela.',
    endConditions: 'reserva confirmada\nlotado\nsó reservas online',
    expectedOutcomes:
      'Mesa reservada para 3 sábado 20h\nAlergia a marisco anotada',
    contingencies:
      'Se 20h não disponível → aceitar entre 19:30 e 21:00\nSe lotado no sábado → perguntar por sexta ou domingo',
    language: 'Portuguese',
  },
  {
    label: '🇵🇹 Cabeleireiro — marcar corte (Portuguese)',
    recipientName: 'Salão Beleza Natural',
    recipientType: 'cabeleireiro',
    purpose:
      'Marcar um corte de cabelo para sábado de manhã. Qualquer horário entre 10h e 13h.',
    scriptGuidance:
      'Preferência pelo mesmo cabeleireiro da última vez (Ricardo), se estiver disponível.',
    endConditions:
      'marcação confirmada\nmarcar online\npróxima disponibilidade informada',
    expectedOutcomes: 'Corte marcado para sábado de manhã\nHorário confirmado',
    contingencies:
      'Se Ricardo não estiver disponível sábado → aceitar outro profissional\nSe não houver manhã → aceitar início da tarde (até 14h)',
    language: 'Portuguese',
  },
  // ── IVR / DTMF Testing ──────────────────────────────────────────────
  {
    label: '🔧 DTMF Test — TheTestCall (UK +44)',
    recipientName: 'TheTestCall IVR',
    recipientType: 'test_ivr',
    purpose:
      'This is an IVR test line. Navigate to option 2 (DTMF testing), then press digits 1, 2, 3, # and listen for the readback confirming the digits were received.',
    scriptGuidance:
      'Main menu options: 1=Record/playback, 2=DTMF testing, 3=Echo test, 8=Real-time DTMF test. Press 2 first, then enter digits 1-2-3-# to verify they register. If option 8 is available, try that for instant readback.',
    endConditions:
      'IVR read back the digits correctly\nDTMF test completed\nCall failed to connect',
    expectedOutcomes:
      'Successfully navigated IVR menu by pressing 2\nEntered test digits 1, 2, 3, #\nIVR read back the entered digits confirming DTMF tones were received',
  },
  {
    label: '🔧 DTMF Test — TheTestCall (US +1)',
    recipientName: 'TheTestCall IVR',
    recipientType: 'test_ivr',
    purpose:
      'This is an IVR test line. Navigate to option 8 (real-time DTMF test) for instant readback of each digit pressed. Press each digit 0-9 one at a time and confirm the IVR reads it back.',
    scriptGuidance:
      'Main menu: 1=Record/playback, 2=DTMF testing, 3=Echo test, 8=Real-time DTMF test. Use option 8 for instant per-digit readback. Press digits one at a time and wait for confirmation.',
    endConditions:
      'IVR confirmed digits were received\nDTMF test completed\nCall failed to connect',
    expectedOutcomes:
      'Successfully navigated IVR to option 8\nPressed individual digits and heard readback for each\nConfirmed DTMF tones are being received by the remote system',
  },
  {
    label: '🔧 DTMF Test — InfoTel (+1 804 222 1111)',
    recipientName: 'InfoTel DTMF Test Line',
    recipientType: 'test_ivr',
    purpose:
      'This is a dedicated DTMF test line. Press 2 for the DTMF test, then after the beep enter digits 1, 2, 3, 4, 5 and press #. The system will read back the digits you pressed.',
    scriptGuidance:
      'Menu: 1=1000hz tone, 2=DTMF test, 3=Voice echo, 4=Repeat back, 5=Fax. Press 2 first. After the beep, press 1-2-3-4-5 then #. The system reads back the digits. This is DTMF-only — no voice recognition.',
    endConditions:
      'System read back the digits correctly\nDTMF test completed\nCall failed to connect',
    expectedOutcomes:
      'Pressed 2 to enter DTMF test mode\nEntered digits 1, 2, 3, 4, 5 followed by #\nSystem read back the entered digits confirming DTMF tones were received',
  },
  {
    label: '🔧 DTMF Test — ResponseiQ (+44 113 403 2330)',
    recipientName: 'ResponseiQ DTMF Test',
    recipientType: 'test_ivr',
    purpose:
      'This is a dedicated DTMF test service. When prompted, press digits on the keypad followed by * or #. The system will read back the digits you pressed.',
    scriptGuidance:
      'The system will prompt you to press digits. Enter any digits (e.g. 1-2-3-4-5) then press * or # to submit. It reads back what you entered. This is DTMF-only — no voice recognition.',
    endConditions:
      'System read back the digits correctly\nDTMF test completed\nCall failed to connect',
    expectedOutcomes:
      'Pressed digits when prompted by the system\nPressed * or # to submit\nSystem read back the entered digits confirming DTMF tones were received',
  },
  {
    label: '🔧 DTMF Test — TheTestCall Toronto (+1 416 342 9562)',
    recipientName: 'TheTestCall DTMF',
    recipientType: 'test_ivr',
    purpose:
      'This is a DTMF test line. Press 8 for real-time DTMF readback — each key you press is instantly read back. Alternatively press 2 for batch mode (enter digits + # for readback).',
    scriptGuidance:
      'Menu: 1=Record/playback, 2=DTMF batch test, 3=Echo, 8=Real-time DTMF readback. Press 8 for instant per-key readback, or 2 for batch. DTMF-only — no voice recognition.',
    endConditions:
      'System read back digits in real-time\nDTMF test completed\nCall failed to connect',
    expectedOutcomes:
      'Pressed 8 to enter real-time DTMF mode\nPressed individual digits and heard instant readback for each\nConfirmed DTMF tones are being received',
  },
  {
    label: '🔧 DTMF Test — Lloyds Bank (+44 345 300 0000)',
    recipientName: 'Lloyds Bank IVR',
    recipientType: 'test_ivr',
    purpose:
      'This is an IVR test. Navigate through their phone menu by pressing digits when prompted. Listen for menu options and press the appropriate key.',
    scriptGuidance:
      'Lloyds will likely say "Press 1 for..." etc. Press any option that keeps you in the menu (e.g. general enquiries). Goal is to verify DTMF tones are received — the IVR advancing to the next menu confirms it worked.',
    endConditions:
      'IVR advanced past the first menu after pressing a digit\nDTMF test completed\nCall failed to connect',
    expectedOutcomes:
      'Successfully pressed a digit when prompted\nIVR responded by advancing to next menu or sub-menu\nConfirmed DTMF navigation works',
  },
  {
    label: '🔧 DTMF Test — British Gas (+44 800 587 5000)',
    recipientName: 'British Gas IVR',
    recipientType: 'test_ivr',
    purpose:
      'This is an IVR test. Navigate through their phone menu by pressing digits when prompted. Listen for menu options and press the appropriate key.',
    scriptGuidance:
      'British Gas will present options like "Press 1 for...", "Press 2 for...". Press any option that leads to another menu rather than a queue for a human. Goal is to verify DTMF tones register.',
    endConditions:
      'IVR advanced past the first menu\nDTMF test completed\nCall failed to connect',
    expectedOutcomes:
      'Successfully pressed a digit when prompted\nIVR responded by advancing to next menu\nConfirmed DTMF navigation works',
  },
  {
    label: '🔧 DTMF Test — Trout Inn (+44 1865 510930)',
    recipientName: 'The Trout Inn',
    recipientType: 'restaurant',
    purpose:
      'Test DTMF against a real restaurant IVR. Their menu says "Press 1 to make a booking, press 2 to speak to the restaurant." Press 2 to reach a human.',
    scriptGuidance:
      'Known IVR structure: "Press 1 to make a booking" / "Press 2 to speak to the restaurant". Press 2. If you get through to a person, just say you called by mistake and apologise. The goal is to verify the keypress registers.',
    endConditions:
      'IVR advanced after pressing 2\nReached a human after pressing 2\nDTMF not registered\nCall failed to connect',
    expectedOutcomes:
      'Pressed 2 when prompted by the IVR menu\nIVR acknowledged the digit and connected to the restaurant (or queue)\nConfirmed DTMF tone was received by the system',
  },
  {
    label: '🔧 DTMF Test — Citibank US (+1 800 444 4444)',
    recipientName: 'Citibank IVR',
    recipientType: 'test_ivr',
    purpose:
      'This is an IVR test. Navigate their automated banking menu by pressing digits when prompted.',
    scriptGuidance:
      'Citibank will likely say "Press 1 for English" or similar. Press 1 for English, then any subsequent option. Goal is to verify DTMF tones are received — advancing past the language menu confirms it.',
    endConditions:
      'IVR advanced past language selection\nDTMF test completed\nCall failed to connect',
    expectedOutcomes:
      'Successfully pressed 1 for English\nIVR responded with the next menu\nConfirmed DTMF navigation works',
  },
];

export default PRESETS;
